import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { STATUS } from "../../design/ui";
import { statusKey, phaseStatusKey } from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { currentAccount } from "../../utils/api";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { RowButton, StatusChip } from "../../components/mui";
import { SkeletonRows } from "../../components/loading";
import {
  MdCheck,
  MdChevronLeft,
  MdCircle,
  MdErrorOutline,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";

/**
 * 時程 — 這週誰要交什麼（設計稿 #screen-week，el-pm-template_4.html L1874-1934）
 *
 * 這一頁取代舊月曆回答「這幾天誰要交東西」。它不是月曆：
 *   · 一天一列，直接寫出「誰要交什麼」，不用格子塞代碼 —— 設計稿明文點名舊月曆
 *     把工項代碼（內部 ID）畫在格子裡，看的人得先去查那串編號是什麼。
 *     所以這一頁從頭到尾不顯示任何 id / issue_key。
 *   · 沒有事情的日子完全不列出來（硬規則 2）。空格子捲過去只是浪費時間，
 *     而且會讓真正有事的那兩天被稀釋掉。
 *
 * 版面：外層不再包一層頁面容器。Layout.js 的內容區已經處理內距，
 * 而 /Schedule 開頭的路由命中 Layout 的 NO_PAGE_CAP，
 * 這裡只拿到內距、不吃 1120px 上限，也不自己寫 max-width。
 *
 * 資料只來自一支既有端點：GET /Schedule/Calendar（帶該週的 startDate/endDate）。
 * 它回的是工項與里程碑混在一起的扁平陣列，兩種都是「這一天要交的東西」，
 * 所以兩種都列 —— 只列工項的話，10/15 要交的那個里程碑在週檢視裡會憑空消失。
 */

/* ===================== 取值與格式 ===================== */

/**
 * 後端的 DateTimeOffset → 當地時區的 "YYYY-MM-DD"。
 *
 * 「哪一天」只能在這裡用 dayjs 照當地日界切一次。直接比 UTC 的話，
 * 台北時間早上八點以前那一天會整段位移，週一到期的東西會掉到上一週去。
 */
function ymdOf(v) {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
}

/**
 * 該日所屬那一週的星期一。
 *
 * 刻意自己算而不用 dayjs 的 startOf("week")：zh-tw locale 沒有指定 weekStart，
 * dayjs 會退回星期日起算，週界就跟設計稿（9/14 一 – 9/20 日）差一天。
 */
function mondayOf(d) {
  const dow = d.day(); // 0 = 星期日
  return d.subtract(dow === 0 ? 6 : dow - 1, "day").startOf("day");
}

/** "2026-09-20" → 「9 月 20 日」；跨年才補年份（與 Today 的 dueLabel 同一種寫法） */
function longDate(ymd) {
  if (!ymd) return "";
  const d = dayjs(ymd);
  if (!d.isValid()) return "";
  return d.isSame(dayjs(), "year") ? d.format("M 月 D 日") : d.format("YYYY 年 M 月 D 日");
}

/** 分隔列的標題：「今天 · 9 月 20 日（星期日）」。星期由 zh-tw locale 給（index.js 已載入） */
function dayHead(ymd, todayYmd) {
  const d = dayjs(ymd);
  if (!d.isValid()) return "";
  const body = `${longDate(ymd)}（${d.format("dddd")}）`;
  return ymd === todayYmd ? `今日 · ${body}` : body;
}

/** 狀態 → StatusChip 的 kind 與前綴符號（與 pages/MyTask/Index.js 的 STATUS_TAG 同一份規則） */
const STATUS_TAG = {
  pending: { kind: "closed", mark: <MdRemove /> },
  progress: { kind: "progress", mark: <MdCircle size="0.6em" /> },
  review: { kind: "warn", mark: <MdErrorOutline /> },
  done: { kind: "done", mark: <MdCheck /> },
  closed: { kind: "closed", mark: <MdRemove /> },
};

/**
 * 右側徽章。規則照 pages/MyTask/Index.js 的 tagOf：
 * 期限（今天要交／已逾期）一律搶下 attention，其餘才顯示狀態。
 * 一列只能講一件事 —— 狀態（進行中）跟期限（今天要交）同時出現的話，
 * 真正要看的那個會被稀釋掉。
 *
 * 逾期用日界比較（字串相等／大小比較），不是「截止時刻 < 現在」：
 * due_date 存的是當天 00:00，用時刻比的話今天到期的東西早上八點就被判成逾期。
 */
function tagOf(e, todayYmd) {
  if (e.statusK !== "done" && e.statusK !== "closed") {
    if (e.dueYmd === todayYmd) return { kind: "attention", mark: <MdWarningAmber />, text: "今日到期" };
    if (e.dueYmd && e.dueYmd < todayYmd) return { kind: "attention", mark: <MdWarningAmber />, text: "已逾期" };
  }
  const s = STATUS_TAG[e.statusK] || STATUS_TAG.pending;
  return { kind: s.kind, mark: s.mark, text: (STATUS[e.statusK] || STATUS.pending).label };
}

/* ===================== 共用零件 ===================== */

/**
 * 全系統通用的清單列（設計稿 L1899-1908）。欄位順序與徽章規則照
 * pages/MyTask/Index.js 的 ElRow：型別徽章＋標題在上，誰／專案／進度在下，
 * 右側是狀態徽章與 ›。
 *
 * 整列是一顆按鈕：可點區域就是整列（遠大於 44px），而且帶文字，
 * 不是只有一顆看不出是什麼的圖示（硬規則 3）。
 */
function ElRow({ attention, typeLabel, title, metas, tag, onClick }) {
  return (
    <RowButton
      tone={attention ? "attention" : ""}
      onClick={onClick}
      title={
        <Stack component="span" direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          {typeLabel ? (
            <StatusChip kind="type" size="small">
              {typeLabel}
            </StatusChip>
          ) : null}
          <span>{title}</span>
        </Stack>
      }
      meta={
        metas.length > 0
          ? metas.map((m, i) => (
              <span key={`${i}-${m}`}>{m}</span>
            ))
          : null
      }
      side={
        <StatusChip kind={tag.kind} mark={tag.mark}>
          {tag.text}
        </StatusChip>
      }
    />
  );
}

ElRow.propTypes = {
  attention: PropTypes.bool,
  typeLabel: PropTypes.string,
  title: PropTypes.string.isRequired,
  metas: PropTypes.array,
  tag: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};
ElRow.defaultProps = { attention: false, typeLabel: "", metas: [], onClick: () => {} };

/* ===================== 畫面 ===================== */

function ScheduleWeek(props) {
  const navigate = useNavigate();
  const { userInfo } = useApp();
  const { GET_ScheduleCalendar } = props;

  /*
   * 今天的日界。用字串當依賴而不是 dayjs 物件：dayjs() 每次 render 都是新物件，
   * 放進依賴陣列等於每次都重算；日期字串只有跨午夜才會變。
   */
  const todayYmd = dayjs().format("YYYY-MM-DD");

  /* 目前看的是哪一週，只記星期一那一天；上一週／下一週就是 ±7 天 */
  const [monday, setMonday] = useState(() => mondayOf(dayjs()).format("YYYY-MM-DD"));
  const sunday = useMemo(() => dayjs(monday).add(6, "day").format("YYYY-MM-DD"), [monday]);
  const isThisWeek = monday === mondayOf(dayjs(todayYmd)).format("YYYY-MM-DD");

  /* 「資料回來了沒」與「這一週真的沒事」要分開：兩者都是 0 筆，
     混在一起的話每次換週都會先閃一下「這一週沒有人要交東西」 */
  const [ready, setReady] = useState(false);

  /** 人員篩選："all" ／ "me" ／ 指派人姓名 */
  const [who, setWho] = useState("all");

  useEffect(() => {
    setReady(false);
    /*
     * endDate 刻意往後多帶一天。
     *
     * 後端的區間條件是 `StartDate <= end || DueDate <= end`，而 end 是
     * DateTimeOffset.Parse("YYYY-MM-DD") ＝ 當天 00:00。星期日下午到期、
     * 又沒有開始日的工項兩個條件都不成立，會被整筆濾掉。
     * 多帶一天換回那批資料，落在週外的部分下面自己再篩一次。
     *
     * loading 參數在 saga 的 finally 呼叫，成功或失敗都會走到，不會卡在載入中。
     */
    GET_ScheduleCalendar(
      { startDate: monday, endDate: dayjs(sunday).add(1, "day").format("YYYY-MM-DD") },
      null,
      (v) => {
        if (!v) setReady(true);
      }
    );
  }, [monday, sunday, GET_ScheduleCalendar]);

  /* ---------- 正規化 ---------- */

  /*
   * /Schedule/Calendar 回的是工項與階段（里程碑）混在一起的扁平陣列。
   * 兩者的欄位名一樣，只有 type 不同，所以在這裡一次攤平成同一個形狀。
   *
   * 階段的 title 被後端硬加了前綴（見 ScheduleService.GetCalendarEvents；現在是「[交付目標] 」，
   * 更早是「[階段] 」，之後可能改成「[里程碑] 」—— 三種都剝），
   * 直接顯示會變成「[階段] 交初版給客戶」——那個中括號是後端拿來區分型別用的，
   * 不是使用者要看的東西，這裡先剝掉再顯示，型別改用型別徽章表達。
   */
  const events = useMemo(() => {
    const raw = props.calendar;
    const arr = Array.isArray(raw) ? raw : _.get(raw, "data_list", []);
    return (Array.isArray(arr) ? arr : [])
      .filter(Boolean)
      .map((e) => {
        const kind = String(_.get(e, "type", _.get(e, "Type", "task")) || "task");
        const isPhase = kind === "phase";
        const title = String(_.get(e, "title", _.get(e, "Title", "")) || "");
        const st = _.get(e, "status", _.get(e, "Status"));
        return {
          id: _.get(e, "id", _.get(e, "Id")),
          isPhase,
          title: (isPhase ? title.replace(/^\[(?:交付目標|里程碑|階段)\]\s*/, "") : title) || "未命名",
          projectName: String(_.get(e, "project_name", _.get(e, "projectName", "")) || ""),
          /* 「要交的那一天」＝ end_date（工項是 due_date、里程碑是 EndDate）。
             兩邊都沒有就退回 start_date，再沒有就不算「這一天要交的東西」 */
          dueYmd: ymdOf(_.get(e, "end_date", _.get(e, "endDate"))) || ymdOf(_.get(e, "start_date", _.get(e, "startDate"))),
          /* 階段的 status 是里程碑自己的 0/1/2，跟工項的 0~4 不是同一組碼 */
          statusK: isPhase ? phaseStatusKey(st) : statusKey(st),
          assignee: String(_.get(e, "assignee_name", _.get(e, "assigneeName", "")) || ""),
          assigneeAccount: String(_.get(e, "assignee", _.get(e, "Assignee", "")) || ""),
          progress: Number(_.get(e, "progress", _.get(e, "Progress", 0)) || 0),
        };
      })
      .filter((e) => e.dueYmd >= monday && e.dueYmd <= sunday);
  }, [props.calendar, monday, sunday]);

  /* ---------- 人員篩選 ---------- */

  /* 下拉的選項只由這一週真的有事的人組成 —— 列出全公司的名單，
     其中九成選下去都是空的，那是一排會讓人白按的選項 */
  const people = useMemo(
    () => [...new Set(events.map((e) => e.assignee).filter(Boolean))].sort((a, b) => a.localeCompare(b, "zh-Hant")),
    [events]
  );

  /* 「我負責的」比對帳號而不是姓名：同名同姓在這個庫裡不是沒有發生過 */
  const myAccount = _.get(userInfo, "account", _.get(userInfo, "Account", "")) || currentAccount();

  const shown = useMemo(() => {
    if (who === "all") return events;
    if (who === "me") return events.filter((e) => myAccount && e.assigneeAccount === myAccount);
    return events.filter((e) => e.assignee === who);
  }, [events, who, myAccount]);

  /* ---------- 分日 ---------- */

  /*
   * 只把「有事的日期」排出來（硬規則 2）。
   * 空日不佔一列，所以這裡不是從星期一數到星期日，而是從資料反推有哪幾天。
   */
  const days = useMemo(() => {
    const bucket = new Map();
    shown.forEach((e) => {
      if (!bucket.has(e.dueYmd)) bucket.set(e.dueYmd, []);
      bucket.get(e.dueYmd).push(e);
    });
    return [...bucket.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([ymd, list]) => ({
        ymd,
        /* 同一天之內：先里程碑後工項（里程碑是那一天的主角），再照標題排，
           順序才不會每次 render 都跳 */
        items: list.sort(
          (x, y) => Number(y.isPhase) - Number(x.isPhase) || x.title.localeCompare(y.title, "zh-Hant")
        ),
      }));
  }, [shown]);

  /*
   * 跳過了幾個空日：末尾那句說明要講得出數字，不然它只是一句空話。
   * 篩了人之後不能再說「沒有人要交東西」—— 那幾天其實有事，只是不是這個人的，
   * 所以兩種情況要各講各的（見下面的 skipNote）。
   */
  const skippedDays = 7 - days.length;

  const rangeText = `${longDate(monday)} – ${longDate(sunday)}`;
  const summary = ready
    ? `${rangeText} · ${shown.length > 0 ? `本週共 ${shown.length} 件到期` : "本週無到期項目"}`
    : `${rangeText} · 載入中…`;

  const skipNote = (() => {
    if (skippedDays <= 0) return "本週每日皆有到期項目。";
    return who === "all"
      ? `本週其餘 ${skippedDays} 天無到期項目，故不列出。`
      : `本週其餘 ${skippedDays} 天無符合篩選條件的項目，故不列出；切換為「全部人員」可檢視其他人員的項目。`;
  })();

  const shiftWeek = (n) => setMonday(dayjs(monday).add(n * 7, "day").format("YYYY-MM-DD"));

  const rowOf = (e) => (
    <ElRow
      key={`${e.isPhase ? "p" : "t"}-${e.id}`}
      attention={e.statusK !== "done" && e.statusK !== "closed" && e.dueYmd <= todayYmd}
      /* 工項的型別（問題修正／工作…）在 /Schedule/Calendar 的投影裡沒有回傳，
         認不得時 typeKey() 一律退回「工作」—— 整排都貼「工作」比沒有徽章更誤導，
         所以工項不貼型別徽章。里程碑的型別是 type 欄位明講的，可以貼。 */
      typeLabel={e.isPhase ? "里程碑" : ""}
      title={e.title}
      /*
       * 欄位順序照 Today：誰 → 專案 → 進度。
       * 少掉 Today 的「要交：X 月 X 日」那一格 —— 這一頁的日期已經寫在上面的
       * 分隔列上了，每一列再寫一次只是把同一個日期重複七次。
       * 沒有的資料整格不畫，不要填「—」充版面（硬規則 10）。
       */
      metas={[
        e.isPhase ? "" : e.assignee ? `負責人：${e.assignee}` : "未指派",
        e.projectName,
        /* 進度 0 的工項寫「還差 100%」等於沒講；100 的已經有 ✓ 徽章了 */
        !e.isPhase && e.progress > 0 && e.progress < 100 ? `尚餘 ${100 - Math.round(e.progress)}%` : "",
      ].filter(Boolean)}
      tag={tagOf(e, todayYmd)}
      onClick={() => {
        /* 里程碑列帶回時程軸：那一頁才看得到整段排程。不帶 phaseId ——
           /Schedule（Goals.js）目前不吃網址參數，帶了也不會選到那個里程碑。 */
        if (e.isPhase) navigate("/Schedule");
        else if (e.id != null) navigate(`/Task/${e.id}`);
      }}
    />
  );

  return (
    <>
      {/* ---------- 回上層（設計稿 L1875-1877） ---------- */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="text" startIcon={<MdChevronLeft aria-hidden="true" />} onClick={() => navigate("/Schedule")}>
          返回里程碑時程軸
        </Button>
      </Stack>

      {/* ---------- 標題與換週（設計稿 L1878-1886） ---------- */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 1 }}>
        <Typography variant="h1" component="h1">
          本週到期項目
        </Typography>
        <Box sx={{ flex: "1 1 auto" }} />
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
          {/* 三顆都有文字、都是 outlined：這一頁沒有哪一個動作比其他兩個更主要，
              整頁也就一顆 contained 都沒有（硬規則 1 是上限不是下限） */}
          <Button variant="outlined" onClick={() => shiftWeek(-1)}>
            上一週
          </Button>
          <Button
            variant="outlined"
            aria-pressed={isThisWeek}
            onClick={() => setMonday(mondayOf(dayjs()).format("YYYY-MM-DD"))}
          >
            本週
          </Button>
          <Button variant="outlined" onClick={() => shiftWeek(1)}>
            下一週
          </Button>
        </Stack>
      </Stack>

      <Typography sx={{ mt: 1.5 }}>{summary}</Typography>

      {/* ---------- 人員篩選（設計稿 L1889-1892）。只有一個下拉，不做多重篩選器 ---------- */}
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", py: 2 }}>
        <Typography component="label" variant="body2" color="text.secondary" htmlFor="week-who">
          篩選人員：
        </Typography>
        <TextField
          select
          id="week-who"
          /* 寬度跟著內容走（設計稿 L1891）：篩選列上的下拉不該橫跨整頁 */
          sx={{ width: "auto", maxWidth: "100%" }}
          slotProps={{ select: { native: true } }}
          value={who}
          onChange={(e) => setWho(e.target.value)}
        >
          <option value="all">全部人員</option>
          {/* 拿不到自己的帳號時不放「我負責的」—— 一個選下去永遠是空的選項比沒有更糟 */}
          {myAccount ? <option value="me">我負責的項目</option> : null}
          {people.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </TextField>
      </Stack>

      {/* ---------- 一天一列（設計稿 L1895-1930） ---------- */}
      {!ready ? <SkeletonRows rows={5} label="本週項目載入中" /> : days.length === 0 ? (
        /* 這一週真的沒事就整塊換成空狀態（硬規則 2），不要畫七個空格子 */
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h2">{who === "all" ? "本週無到期項目" : "該人員本週無到期項目"}</Typography>
          <Typography sx={{ mt: 1, mb: 2 }}>
            {"僅列出已設定到期日的工項與里程碑。未設定到期日的項目不會出現在任何一週，請至工項補上日期。"}
          </Typography>
          <Button variant="text" onClick={() => shiftWeek(1)}>
            檢視下一週
          </Button>
        </Box>
      ) : (
        <>
          <Card>
            {days.map((d) => (
              <React.Fragment key={d.ymd}>
                {/* 日期分隔列：列要貼齊卡緣，分組標頭的內距與底色照設計稿 L1897 */}
                <Box sx={{ px: 3, py: 2, bgcolor: "var(--surface-sunken)" }}>
                  <Typography variant="h3" component="p">
                    {dayHead(d.ymd, todayYmd)}
                  </Typography>
                </Box>
                {d.items.map(rowOf)}
              </React.Fragment>
            ))}
          </Card>

          {/* 為什麼只有這幾天：不說的話，看的人會以為是資料沒載完（設計稿 L1932） */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {skipNote}
          </Typography>
        </>
      )}
    </>
  );
}

/*
 * 資料來源：GET /Schedule/Calendar（帶該週的 startDate/endDate）→ state.Schedule.Calendar。
 * 刻意不帶 projectId：這一頁問的是「這週誰要交什麼」，跨專案才答得完整，
 * 列上的專案名就是用來分辨哪一件屬於哪個案子的。
 *
 * 誠實的缺口（畫面上一律不編造，缺哪個就整格不渲染）：
 *   1. 工項型別 —— /Schedule/Calendar 的投影沒有 issue_type_key，所以工項不貼型別徽章。
 *   2. 里程碑的負責人 —— 後端固定回 assignee_name = null，里程碑列不寫「誰要交」。
 */
const mapStateToProps = (state) => ({
  calendar: _.get(state, "Schedule.Calendar", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ScheduleCalendar: (payload, callback, loading) =>
    dispatch({ type: "GET_ScheduleCalendar", payload, callback, loading }),
});

ScheduleWeek.propTypes = {
  calendar: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_ScheduleCalendar: PropTypes.func,
};

ScheduleWeek.defaultProps = {
  calendar: [],
  GET_ScheduleCalendar: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleWeek);
